import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, RefreshCw, AlertCircle, Maximize2, Minimize2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsAdmin } from '@/hooks/useAdmin'
import { useAdminStats } from '@/hooks/useAdminStats'
import { MetricCard } from './stats/MetricCard'
import { ToplineCard } from './stats/ToplineCard'
import { LargeChart } from './stats/LargeChart'
import { WorldMap } from './stats/WorldMap'

type TimePeriod = 1 | 7 | 30

const AUTO_REFRESH_INTERVAL = 60 * 60 * 1000 // 1 hour
const theseusLogo = '/theseus-logo.png'

function formatTimeAgo(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMins = Math.floor(diffMs / 60000)
  
  if (diffMins < 1) return 'just now'
  if (diffMins === 1) return '1 minute ago'
  if (diffMins < 60) return `${diffMins} minutes ago`
  
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours === 1) return '1 hour ago'
  return `${diffHours} hours ago`
}

function formatCurrentDateTime(): string {
  const now = new Date()
  const date = now.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const time = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
  return `${date} · ${time}`
}

// Isolated clock component - only this re-renders every second
function LiveClock() {
  const [, setTick] = useState(0)
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <span 
      className="text-sm text-muted-foreground tabular-nums"
      style={{ fontFamily: '"Geist Mono", monospace' }}
    >
      {formatCurrentDateTime()}
    </span>
  )
}

export function StatsPage() {
  const [period] = useState<TimePeriod>(30)
  const [, setTick] = useState(0) // Force re-render for time ago updates
  const [isFullscreen, setIsFullscreen] = useState(false)
  
  // Admin check
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()
  
  // Fetch stats
  const { data, isLoading, isFetching, isError, error, refetch, dataUpdatedAt } = useAdminStats()
  
  // Auto-refresh every hour
  useEffect(() => {
    const interval = setInterval(() => {
      refetch()
    }, AUTO_REFRESH_INTERVAL)
    
    return () => clearInterval(interval)
  }, [refetch])
  
  // Update "time ago" display every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1)
    }, 60000)
    
    return () => clearInterval(interval)
  }, [])

  // Handle escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen])

  // Prevent body scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  // Loading state
  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Access denied
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Access denied</p>
          <p className="text-xs text-muted-foreground/50 mt-1">
            You don't have permission to view this page
          </p>
        </div>
      </div>
    )
  }

  // Shared button styles
  const buttonStyles = cn(
    'flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]',
    'text-muted-foreground hover:text-foreground hover:border-white/[0.1] hover:bg-white/[0.03]',
    'transition-all duration-150 disabled:cursor-not-allowed'
  )

  // Topline metrics component (reused in both layouts)
  const ToplineMetrics = ({ className }: { className?: string }) => (
    <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', className)}>
      <ToplineCard
        title="Maps This Week"
        value={data?.maps.timeseries?.slice(-7).reduce((sum, p) => sum + p.count, 0) ?? 0}
        previousValue={data?.maps.timeseries?.slice(-14, -7).reduce((sum, p) => sum + p.count, 0)}
        previousLabel="prev. period"
        loading={isLoading}
      />
      <ToplineCard
        title="Flights This Week"
        value={data?.flights.timeseries?.slice(-7).reduce((sum, p) => sum + p.count, 0) ?? 0}
        previousValue={data?.flights.timeseries?.slice(-14, -7).reduce((sum, p) => sum + p.count, 0)}
        previousLabel="prev. period"
        loading={isLoading}
      />
      <ToplineCard
        title="Sign Ups This Week"
        value={(() => {
          const ts = data?.users.timeseries ?? []
          const latest = ts[ts.length - 1]?.cumulative ?? 0
          const weekAgo = ts[ts.length - 8]?.cumulative ?? 0
          return latest - weekAgo
        })()}
        previousValue={(() => {
          const ts = data?.users.timeseries ?? []
          const weekAgo = ts[ts.length - 8]?.cumulative ?? 0
          const twoWeeksAgo = ts[ts.length - 15]?.cumulative ?? 0
          return weekAgo - twoWeeksAgo
        })()}
        previousLabel="prev. period"
        loading={isLoading}
      />
    </div>
  )

  // Trends section component (reused in both layouts)
  const TrendsSection = ({ compactCharts = false }: { compactCharts?: boolean }) => (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-foreground">Trends</h2>
        <p className="text-xs text-muted-foreground/50 mt-0.5">
          User activity trends
        </p>
      </div>

      {/* MAU Chart */}
      <LargeChart
        title="Monthly Active Users"
        subtitle="Unique users with activity per month"
        data={data?.monthlyActiveUsers?.timeseries ?? []}
        type="monthly"
        loading={isLoading}
        height={compactCharts ? 200 : 280}
        color="#3b82f6"
      />

      {/* Daily Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          title="Maps Generated"
          timeseries={data?.maps.timeseries ?? []}
          loading={isLoading}
          period={period}
        />
        <MetricCard
          title="Flights"
          timeseries={data?.flights.timeseries ?? []}
          loading={isLoading}
          period={period}
        />
        <MetricCard
          title="New Users"
          timeseries={(() => {
            const ts = data?.users.timeseries ?? []
            return ts.map((point, i) => ({
              date: point.date,
              count: i === 0 ? 0 : point.cumulative - (ts[i - 1]?.cumulative ?? 0)
            }))
          })()}
          loading={isLoading}
          period={period}
        />
      </div>
    </div>
  )

  // Visits section component (reused in both layouts)
  const VisitsSection = () => (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-foreground">Visits</h2>
        <p className="text-xs text-muted-foreground/50 mt-0.5">
          Page visits and sign-ins (past 30 days)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Dashboard"
          timeseries={data?.visits?.dashboard?.timeseries ?? []}
          total={data?.visits?.dashboard?.total ?? 0}
          suffix="visits"
          loading={isLoading}
          period={period}
        />
        <MetricCard
          title="Docs"
          timeseries={data?.visits?.docs?.timeseries ?? []}
          total={data?.visits?.docs?.total ?? 0}
          suffix="visits"
          loading={isLoading}
          period={period}
        />
        <MetricCard
          title="Vozilla Sign-ins"
          timeseries={data?.visits?.vozillaSignins?.timeseries ?? []}
          total={data?.visits?.vozillaSignins?.total ?? 0}
          suffix="sign-ins"
          loading={isLoading}
          period={period}
        />
      </div>
    </div>
  )

  // World Map section component
  const WorldMapSection = ({ mapHeight }: { mapHeight?: number }) => (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-medium text-foreground">Vozilla User Distribution</h2>
        <p className="text-xs text-muted-foreground/50 mt-0.5">
          Geographic distribution of registered users
        </p>
      </div>

      {isLoading ? (
        <div 
          className="rounded-lg border border-white/[0.06] bg-white/[0.02] flex items-center justify-center"
          style={{ height: mapHeight ?? 300 }}
        >
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
        </div>
      ) : (
        <WorldMap data={data?.users.byCountry ?? {}} height={mapHeight} />
      )}
    </div>
  )

  // Error banner component
  const ErrorBanner = () => (
    isError ? (
      <div className="flex items-center gap-3 p-4 rounded-lg border border-red-400/20 bg-red-400/5">
        <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm text-red-400">Failed to load statistics</p>
          <p className="text-xs text-muted-foreground/70 mt-0.5">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="ml-auto text-xs text-red-400 hover:text-red-300 underline"
        >
          Retry
        </button>
      </div>
    ) : null
  )

  // Fullscreen-specific: Left column with trends
  const FullscreenChartsColumn = () => (
    <div className="space-y-4 h-full overflow-y-auto pr-3">
      {/* Trends Section */}
      <div>
        <h2 className="text-sm font-medium text-foreground">Trends</h2>
        <p className="text-xs text-muted-foreground/50 mt-0.5">User activity trends</p>
      </div>

      {/* MAU Chart - full row */}
      <LargeChart
        title="Monthly Active Users"
        subtitle="Unique users with activity per month"
        data={data?.monthlyActiveUsers?.timeseries ?? []}
        type="monthly"
        loading={isLoading}
        height={180}
        color="#3b82f6"
        periodLabel=" "
      />

      {/* Maps / Flights Charts - 2 per row */}
      <div className="grid grid-cols-2 gap-4">
        <LargeChart
          title="Maps Generated"
          subtitle="Maps created per day"
          data={data?.maps.timeseries ?? []}
          type="daily"
          loading={isLoading}
          height={180}
          total={data?.maps.timeseries?.reduce((sum, p) => sum + p.count, 0) ?? 0}
          totalLabel="30d Total"
          periodLabel=" "
        />
        <LargeChart
          title="Flights"
          subtitle="Flights logged per day"
          data={data?.flights.timeseries ?? []}
          type="daily"
          loading={isLoading}
          height={180}
          color="#f59e0b"
          total={data?.flights.timeseries?.reduce((sum, p) => sum + p.count, 0) ?? 0}
          totalLabel="30d Total"
          periodLabel=" "
        />
      </div>

      {/* Total Users - full width chart showing cumulative growth */}
      <LargeChart
        title="Total Users"
        subtitle="Cumulative registered users"
        data={(() => {
          const ts = data?.users.timeseries ?? []
          return ts.map((point) => ({
            date: point.date,
            count: point.cumulative
          }))
        })()}
        type="daily"
        loading={isLoading}
        height={180}
        color="#8b5cf6"
        total={data?.users?.total ?? 0}
        totalLabel="Total"
        periodLabel=" "
      />
    </div>
  )

  // Fullscreen-specific: Right column with map and visits
  const FullscreenMapColumn = () => (
    <div className="h-full overflow-y-auto space-y-4">
      {/* World Map */}
      <WorldMapSection mapHeight={600} />

      {/* Visits Section */}
      <div>
        <h2 className="text-sm font-medium text-foreground">Visits</h2>
        <p className="text-xs text-muted-foreground/50 mt-0.5">Page visits and sign-ins (past 30 days)</p>
      </div>

      {/* Visits Charts - 3 per row */}
      <div className="grid grid-cols-3 gap-4">
        <LargeChart
          title="Dashboard"
          subtitle="Page visits per day"
          data={data?.visits?.dashboard?.timeseries ?? []}
          type="daily"
          loading={isLoading}
          height={180}
          color="#06b6d4"
          total={data?.visits?.dashboard?.total ?? 0}
          totalLabel="30d Total"
          periodLabel=" "
        />
        <LargeChart
          title="Docs"
          subtitle="Page visits per day"
          data={data?.visits?.docs?.timeseries ?? []}
          type="daily"
          loading={isLoading}
          height={180}
          color="#10b981"
          total={data?.visits?.docs?.total ?? 0}
          totalLabel="30d Total"
          periodLabel=" "
        />
        <LargeChart
          title="Vozilla Sign-ins"
          subtitle="App sign-ins per day"
          data={data?.visits?.vozillaSignins?.timeseries ?? []}
          type="daily"
          loading={isLoading}
          height={180}
          color="#ec4899"
          total={data?.visits?.vozillaSignins?.total ?? 0}
          totalLabel="30d Total"
          periodLabel=" "
        />
      </div>
    </div>
  )

  // Fullscreen overlay
  const FullscreenOverlay = () => createPortal(
    <div className="fixed inset-0 z-50 bg-background overflow-hidden animate-fade-in flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 h-14 flex items-center justify-between px-6">
        {/* Logo */}
        <a
          href="https://theseus.us"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img src={theseusLogo} alt="Theseus" className="h-6" />
        </a>

        {/* Right side: Date + Controls */}
        <div className="flex items-center gap-4">
          {/* Current date and time in Geist Mono */}
          <LiveClock />

          {/* Refresh button */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className={buttonStyles}
            title="Refresh data"
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            {dataUpdatedAt > 0 && (
              <span className="text-xs text-muted-foreground/70">
                {isFetching ? 'Refreshing...' : formatTimeAgo(dataUpdatedAt)}
              </span>
            )}
          </button>

          {/* Exit fullscreen button */}
          <button
            onClick={() => setIsFullscreen(false)}
            className={buttonStyles}
            title="Exit fullscreen (Esc)"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Topline Metrics - Full width row */}
      <div className="flex-shrink-0 px-16 py-6">
        <ErrorBanner />
        <div className="grid grid-cols-3 gap-6">
          <ToplineCard
            title="Maps This Week"
            value={data?.maps.timeseries?.slice(-7).reduce((sum, p) => sum + p.count, 0) ?? 0}
            previousValue={data?.maps.timeseries?.slice(-14, -7).reduce((sum, p) => sum + p.count, 0)}
            previousLabel="prev. period"
            loading={isLoading}
            size="large"
          />
          <ToplineCard
            title="Flights This Week"
            value={data?.flights.timeseries?.slice(-7).reduce((sum, p) => sum + p.count, 0) ?? 0}
            previousValue={data?.flights.timeseries?.slice(-14, -7).reduce((sum, p) => sum + p.count, 0)}
            previousLabel="prev. period"
            loading={isLoading}
            size="large"
          />
          <ToplineCard
            title="Sign Ups This Week"
            value={(() => {
              const ts = data?.users.timeseries ?? []
              const latest = ts[ts.length - 1]?.cumulative ?? 0
              const weekAgo = ts[ts.length - 8]?.cumulative ?? 0
              return latest - weekAgo
            })()}
            previousValue={(() => {
              const ts = data?.users.timeseries ?? []
              const weekAgo = ts[ts.length - 8]?.cumulative ?? 0
              const twoWeeksAgo = ts[ts.length - 15]?.cumulative ?? 0
              return weekAgo - twoWeeksAgo
            })()}
            previousLabel="prev. period"
            loading={isLoading}
            size="large"
          />
        </div>
      </div>

      {/* Main content - Two column layout */}
      <div className="flex-1 min-h-0 flex px-16 py-4 gap-10">
        {/* Left column - Charts (50%) */}
        <div className="w-1/2">
          <FullscreenChartsColumn />
        </div>

        {/* Right column - Map + Visits (50%) */}
        <div className="w-1/2">
          <FullscreenMapColumn />
        </div>
      </div>
    </div>,
    document.body
  )

  // Normal layout
  return (
    <>
      {isFullscreen && <FullscreenOverlay />}

      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-medium text-foreground tracking-tight">Statistics</h1>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Refresh button with last updated time */}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className={buttonStyles}
              title="Refresh data"
            >
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              {dataUpdatedAt > 0 && (
                <span className="text-xs text-muted-foreground/70">
                  {isFetching ? 'Refreshing...' : formatTimeAgo(dataUpdatedAt)}
                </span>
              )}
            </button>

            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(true)}
              className={buttonStyles}
              title="Expand to fullscreen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <ErrorBanner />
        <ToplineMetrics />
        <TrendsSection />
        <VisitsSection />
        <WorldMapSection />
      </div>
    </>
  )
}
